import type { GenericOrderItem, SubscriptionSimulationInfoType } from 'app/src/model'
import type { catalogSummaryApi, membershipApi } from 'app/src/services/welfareportal'
import type { WebAppContextType } from 'app/src/shared/helpers/context'
import CodiceFiscale from 'codice-fiscale-js'
import type { AutoCompleteSelectEvent } from 'primereact/autocomplete'

/**
 * Converte una stringa in Pascal Case.
 * Gestisce:
 * - lettere Unicode
 * - spazi
 * - apostrofi
 * - trattini
 *
 * @param str una stringa da convertire in pascal case
 * @returns la stringa convertita in pascal case o null se la stringa è vuota o non definita
 */
export function toPascalCase(str?: string): string | null {
  if (!str) return null
  // Normalizza la stringa per gestire correttamente lettere accentate e combinazioni Unicode
  str = str.normalize('NFC')
  const capitalize = (word: string): string => {
    return word.toLowerCase().replace(/(^|[.'\u2019-])(\p{L}\p{M}*)/gu, (_, prefix, letter) => prefix + letter.toUpperCase())
  }
  // fallback se Intl.Segmenter non esiste
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' })
    return [...segmenter.segment(str)].map(({ segment, isWordLike }) => (isWordLike ? capitalize(segment) : segment)).join('')
  }
  // Matcha parole Unicode (con eventuali apostrofi o trattini interni)
  return str
    .split(/[\s]/)
    .map((word) => capitalize(word))
    .join(' ')
}

export const euroFormatter = Intl.NumberFormat('in-En', {
  style: 'currency',
  currency: 'EUR',
})
export const euroFormatterIt = Intl.NumberFormat('it', {
  style: 'currency',
  currency: 'EUR',
})

export const decimalFormatter = Intl.NumberFormat('it', {
  style: 'decimal',
  currency: 'EUR',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
})

export const formatDateLong = (iso: string) => {
  try {
    const d = new Date(iso)
    // Show dd/mm/yyyy
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return iso
  }
}

export const formatDateWithShortMonth = (iso: string) => {
  try {
    const d = new Date(iso)
    const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic']
    const day = d.getDate()
    const month = months[d.getMonth()]
    const year = d.getFullYear()
    return `${day} ${month} ${year}`
  } catch {
    return iso
  }
}

export function obfuscateCurrency(
  amount: string,
  visibleDigitsIntegerPart: number = 0,
  visibleDigitsDecimalPart: number = 0,
): string {
  // Split the amount into parts
  const [, numberPart] = amount.split('€')
  const [integerPart, decimalPart] = numberPart.trim().split(',')

  // Remove thousand separators to count total digits
  const plainIntegerPart = integerPart.replace(/\./g, '')
  const totalIntegerDigits = plainIntegerPart.length

  // Handle visibleDigits being 0 or greater than the length of the integer part
  const adjustedVisibleDigits = Math.min(Math.max(visibleDigitsIntegerPart, 0), totalIntegerDigits)

  // Obfuscate the integer part while keeping the thousand separators visible
  let obfuscatedIntegerPart = ''
  let visibleCount = 0

  for (let i = integerPart.length - 1; i >= 0; i--) {
    if (integerPart[i] === '.') {
      obfuscatedIntegerPart = '.' + obfuscatedIntegerPart
    } else if (visibleCount < adjustedVisibleDigits) {
      obfuscatedIntegerPart = integerPart[i] + obfuscatedIntegerPart
      visibleCount++
    } else {
      obfuscatedIntegerPart = '*' + obfuscatedIntegerPart
    }
  }

  // Handle visibleDigits being 0 or greater than the length of the integer part
  const adjustedVisibleDecimalDigits = Math.min(Math.max(visibleDigitsDecimalPart, 0), decimalPart.length)
  // Obfuscate the decimal part
  let obfuscatedDecimalPart = ''
  if (adjustedVisibleDecimalDigits == 0) obfuscatedDecimalPart = '*'.repeat(decimalPart.length)
  else
    obfuscatedDecimalPart =
      '*'.repeat(decimalPart.length - adjustedVisibleDecimalDigits) + decimalPart.slice(-adjustedVisibleDecimalDigits)

  // Return the formatted obfuscated amount
  return `€${obfuscatedIntegerPart},${obfuscatedDecimalPart}`
}

export function removeQuotes(s: string | undefined) {
  return s?.replace(/"/g, "'")?.replace(/^'+|'+$/g, '')
}

export function calculatePercentage(tot: number, num: number): number {
  const percentuale: number = +((num / tot) * 100).toFixed(0)
  return percentuale
}

export function getRandomElement<T>(arr: T[]): T {
  const randomIndex = Math.floor(Math.random() * arr.length)
  return arr[randomIndex]
}

export const replaceUrlInString = (str?: string /* label?: string */): string => {
  if (!str) return ''
  return str
  /* if (!label) return str
  return str.replace(
    /<a([^>]*)>(.*?)<\/a>/gi,
    `<a$1 class='text-[var(--primary-color)] hover:text-[var(--primary-600)]'>${label}</a>`,
  ) */
}

export function getInitials(fullName: string): string {
  const nameParts = fullName.trim().split(' ')

  // Get the first letter of the first and last words
  const initials = nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)

  return initials.toUpperCase()
}

export function extractBirthDate(codiceFiscale: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const months: any = {
    A: '01',
    B: '02',
    C: '03',
    D: '04',
    E: '05',
    H: '06',
    L: '07',
    M: '08',
    P: '09',
    R: '10',
    S: '11',
    T: '12',
  }

  const yearPart = codiceFiscale.slice(6, 8)
  const monthPart = codiceFiscale[8]?.toUpperCase()
  const dayPart = parseInt(codiceFiscale.slice(9, 11))

  const year = parseInt(yearPart, 10)
  const century = year >= 0 && year <= 29 ? '20' : '19' // Heuristic: assume 1900-1999 or 2000-2029

  const yearFull = `${century}${yearPart}`
  const month = months[monthPart]

  const day = dayPart > 40 ? dayPart - 40 : dayPart
  const dayFormatted = String(day).padStart(2, '0')

  return `${yearFull}-${month}-${dayFormatted}`
}

export function isDemoApp(): boolean {
  return import.meta.env.MODE === 'development' && import.meta.env.VITE_MSW_BYPASS
}

export const SESSION_HIDE_LABEL = 'hideDemoLabel'

export function shouldShowDemoLabel(): boolean {
  // Source of truth: solo URL parameter
  const searchParams = window.location.search
  const hashParams = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : ''
  const urlParams = new URLSearchParams(searchParams || hashParams)
  const hideViaQueryString = urlParams.get('hideDemoLabel') === 'true'
  return isDemoApp() && !hideViaQueryString
}

export function calculateAge(birthDate: Date): number {
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()

  return age
}

export function calculateAgeAccurate(birthDate: Date): number {
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  const dayDiff = today.getDate() - birthDate.getDate()

  // Adjust age if the birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--
  }

  return age
}

export const calculateSimulationInfo = (
  percentage: number,
  companyInfo: membershipApi.MembershipExtended,
): SubscriptionSimulationInfoType | undefined => {
  if (companyInfo) {
    const selectedPercentage = percentage
    const conversioneInCredito = companyInfo.membershipInfo.bonusAmount * selectedPercentage
    let premioNonConvertito = companyInfo.membershipInfo.bonusAmount * (1 - companyInfo.membershipInfo.inpsPercentage)
    premioNonConvertito -= premioNonConvertito * companyInfo.membershipInfo.taxPercentage

    let integrazionePoste = 0
    const importoBustaPaga = (1 - selectedPercentage) * premioNonConvertito

    if (selectedPercentage > 0) {
      let rangeFound = false
      let currentPrize: membershipApi.Prize | null = null
      companyInfo.membershipInfo.companyPrize?.forEach((companyPrize) => {
        for (let i: number = 0; i < companyPrize.prizes?.length; i++) {
          currentPrize = companyPrize.prizes[i]
          if (
            (currentPrize.rangeType === 'percentage' &&
              (selectedPercentage > currentPrize.range?.from ||
                (currentPrize.range?.fromInclusive && selectedPercentage === currentPrize.range?.from)) &&
              (typeof currentPrize.range?.to === 'undefined' ||
                selectedPercentage < currentPrize.range?.to ||
                (currentPrize.range?.toInclusive && selectedPercentage === currentPrize.range?.to))) ||
            (currentPrize.rangeType === 'amount' &&
              (conversioneInCredito > currentPrize.range?.from ||
                (currentPrize.range?.fromInclusive && conversioneInCredito === currentPrize.range?.from)) &&
              (typeof currentPrize.range?.to === 'undefined' ||
                conversioneInCredito < currentPrize.range?.to ||
                (currentPrize.range?.toInclusive && conversioneInCredito === currentPrize.range?.to)))
          ) {
            if (currentPrize.prizeType === 'percentage') {
              integrazionePoste += currentPrize.prize * conversioneInCredito
            } else {
              integrazionePoste += currentPrize.prize
            }
            rangeFound = true
          }
          if (rangeFound) break
        }
        rangeFound = false
      })
    }
    return {
      conversioneInCredito,
      importoBustaPaga,
      integrazionePoste,
      premioConvertito: conversioneInCredito + integrazionePoste + importoBustaPaga,
      premioNonConvertito,
      totaleNetto: conversioneInCredito + integrazionePoste + importoBustaPaga,
    }
  } else return undefined
}

export const catalogVisibilityFunc = (
  catalogSummary: catalogSummaryApi.CatalogSummaryItem,
  context: WebAppContextType,
  applyHideCard: boolean,
) => {
  return (
    (!applyHideCard || typeof catalogSummary.hideCard === 'undefined' || !catalogSummary.hideCard) &&
    (context.userInfo.welfareCampaign.configuration.funnels?.some((y) => y === catalogSummary.id) ?? false)
  )
}

export const activeCampaigns = (items: membershipApi.UserMembership[]): membershipApi.UserMembership[] => {
  return items.filter((campaign) => campaign.active === true)
}

export const getShowcaseLink = ({
  macroCategoryId,
  serviceCategory,
  selectedCategory,
  discount,
  breadCrumb,
}: {
  macroCategoryId?: string
  serviceCategory?: string
  selectedCategory?: string
  breadCrumb?: string[]
  discount?: boolean
}) => {
  return {
    macroCategoryId,
    serviceCategory,
    selectedCategory,
    breadCrumb,
    discount,
  }
}

export const getFunnelDiscount = (context: WebAppContextType) => {
  const funnels = context.userInfo?.welfareCampaign?.configuration?.funnels

  if (!funnels) {
    return { haveDiscount: false, onlyDiscount: false }
  }

  const haveDiscount = funnels.includes('DISCOUNT')

  return {
    haveDiscount,
    onlyDiscount: haveDiscount && funnels.length === 1,
  }
}

export const getValueFromTypology = (type: 'percentage' | 'fixed' | 'amount' | undefined, value?: number): string => {
  if (!value) return ''
  if (type === 'percentage') {
    return `${Math.round(value * 100)}%`
  } else if (type === 'fixed' || type === 'amount') {
    return euroFormatterIt.format(value)
  }
  return ''
}

export const calculateDisplayValue = (item: GenericOrderItem): string => {
  if (item.productTypology === 'discount') {
    return item.discountValue || '-'
  }

  return typeof item.price !== 'undefined' ? (item.price === 0 ? 'Gratuito' : euroFormatterIt.format(item.price)) : '-'
}

export const getFromSessionStorage = (key: string) => {
  return sessionStorage.getItem(key)
}

export const setToSessionStorage = (key: string, value: string) => {
  sessionStorage.setItem(key, value)
}

export const isDiscount = (searchParams: URLSearchParams) => {
  return searchParams.get('macroCategoryId') === 'DISCOUNT'
}

const sanitize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // rimuove accenti
    .toUpperCase()
    .replace(/[^A-Z]/g, '') // tieni solo lettere

const extractConsonants = (s: string) => sanitize(s).replace(/[AEIOU]/g, '') // Y è consonante

const extractVowels = (s: string) => sanitize(s).replace(/[^AEIOU]/g, '')

const encodeSurname = (surname: string) => {
  const cons = extractConsonants(surname)
  const vows = extractVowels(surname)
  return (cons + vows + 'XXX').slice(0, 3)
}

const encodeName = (name: string) => {
  const cons = extractConsonants(name)
  if (cons.length >= 4) {
    // regola speciale nomi: 1a, 3a, 4a consonante
    return cons[0] + cons[2] + cons[3]
  }
  const vows = extractVowels(name)
  return (cons + vows + 'XXX').slice(0, 3)
}

const validateName = (name: string, cfName: string) => {
  return encodeName(name) === cfName
}

const validateSurname = (surname: string, cfSurname: string) => {
  return encodeSurname(surname) === cfSurname
}

const validateBirthdate = (dob: string, cfDob: string) => {
  const dobDate = new Date(dob)
  const cfDobDate = new Date(cfDob)

  return (
    dobDate.getFullYear() === cfDobDate.getFullYear() &&
    dobDate.getMonth() === cfDobDate.getMonth() &&
    dobDate.getDate() === cfDobDate.getDate()
  )
}

const validateGender = (gender: string, cfGender: string) => {
  return gender === cfGender
}

export const validateFiscalCode = (
  fiscalCode: string,
  name?: string,
  surname?: string,
  dob?: string,
  gender?: string,
): { isValid: boolean; isAdult: boolean } => {
  const normalizedFC = fiscalCode.toUpperCase()

  // Check fiscal code format (16 characters)
  if (normalizedFC.length !== 16) {
    return { isValid: false, isAdult: false }
  }

  // Check if fiscal code contains only valid characters
  const fcRegex = /^[A-Z0-9]+$/
  if (!fcRegex.test(normalizedFC)) {
    return { isValid: false, isAdult: false }
  }

  try {
    // Extract and validate birth date
    const birthDateString = extractBirthDate(normalizedFC)
    const birthDate = new Date(birthDateString)
    // Check if person is adult (18+)
    const age = calculateAgeAccurate(birthDate)
    const isAdult = age >= 18

    // Check if birth date is valid
    if (isNaN(birthDate.getTime())) {
      return { isValid: false, isAdult }
    }

    const cf = new CodiceFiscale(fiscalCode)

    // Ensure proper date format with zero padding for Safari compatibility
    const cfMonth = String(cf.month).padStart(2, '0')
    const cfDay = String(cf.day).padStart(2, '0')
    const cfDateFormatted = `${cf.year}-${cfMonth}-${cfDay}`

    const isNameValid = name ? validateName(name, cf.name) : true
    const isSurnameValid = surname ? validateSurname(surname, cf.surname) : true
    const isBirthDateValid = dob ? validateBirthdate(dob, cfDateFormatted) : true
    const isGenderValid = gender ? validateGender(gender, cf.gender) : true

    return { isValid: cf.isValid() && isNameValid && isSurnameValid && isBirthDateValid && isGenderValid, isAdult }
  } catch {
    return { isValid: false, isAdult: false }
  }
}

export const formatBalanceDisplay = (balance: number) => {
  const formatted = euroFormatterIt.format(balance).replace('€', '').trim()
  const [integer, decimal] = formatted.split(',')

  return {
    integer,
    decimal,
  }
}

export const VOUCHER_CALCULATOR_CONSTANTS = {
  MAX_TARGET_AMOUNT: 9999.99,
  MIN_TARGET_AMOUNT: 0,
  DEFAULT_PLACEHOLDER: '0,00€',
  CURRENCY: 'EUR',
  LOCALE: 'it-IT',
} as const

export const getAddressFromCoordinates = async (
  latitude?: number,
  longitude?: number,
  clientId?: string,
  mapToken?: string,
  subscriptionKey?: string,
): Promise<string | undefined> => {
  try {
    const baseUrl = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&query=${latitude},${longitude}`

    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }

    let url = baseUrl

    // Priorità al token JWT se disponibile
    if (mapToken) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${mapToken}`,
        ...(clientId ? { 'x-ms-client-id': clientId } : {}),
      }
    } else if (subscriptionKey) {
      url = `${baseUrl}&subscription-key=${subscriptionKey}`
    } else {
      throw new Error('Autenticazione richiesta: fornire mapToken o subscriptionKey')
    }

    const response = await fetch(url, fetchOptions)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (data.addresses && data.addresses.length > 0) {
      const address = data.addresses[0].address
      return `${address.streetName || ''} ${address.streetNumber || ''}, ${address.municipality || ''}, ${address.country || ''}`.trim()
    } else if (data.results && data.results.length > 0) {
      return data.results
    }

    return undefined
  } catch (error) {
    console.error('Error getting address:', error)
    return undefined
  }
}

export const getResultsFromAddress = async (
  query: string,
  clientId?: string,
  mapToken?: string,
  subscriptionKey?: string,
): Promise<AutoCompleteSelectEvent[] | undefined> => {
  try {
    const baseUrl = `https://atlas.microsoft.com/search/address/json?api-version=1.0&query=${encodeURIComponent(query)}&limit=5&countrySet=IT`

    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }

    let url = baseUrl

    // Priorità al token JWT se disponibile
    if (mapToken) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${mapToken}`,
        ...(clientId ? { 'x-ms-client-id': clientId } : {}),
      }
    } else if (subscriptionKey) {
      url = `${baseUrl}&subscription-key=${subscriptionKey}`
    } else {
      throw new Error('Autenticazione richiesta: fornire mapToken o subscriptionKey')
    }

    const response = await fetch(url, fetchOptions)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (data.results && data.results.length > 0) {
      return data.results
    }

    return undefined
  } catch (error) {
    console.error('Error getting address:', error)
    return undefined
  }
}

export const getCurrentLocation = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(position)
      },
      (error) => {
        reject(error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minuti di cache
      },
    )
  })
}

export const getCategoriesUrl = (kind: string, discount: boolean, parentCategoryId: string, categoryId: string) => {
  console.log('getCategoriesUrl', { kind, discount, parentCategoryId, categoryId })

  const kindUrl = !kind ? 'none/' : `${kind}/`
  // const discountUrl = discount ? 'discount' + (!categoryId && !parentCategoryId ? '' : '/') : ''
  const parentCategoryUrl = !parentCategoryId ? '' : parentCategoryId + (!categoryId ? '' : '/')
  const categoryUrl = !categoryId ? '' : categoryId

  console.log('getCategoriesUrl result', `kindUrl:${kind} parentCategoryUrl:${parentCategoryUrl} categoryUrl:${categoryUrl}`)
  console.log('url key', `${kindUrl}${parentCategoryUrl}${categoryUrl}`)
  return `${kindUrl}${parentCategoryUrl}${categoryUrl}`
}

/**
 * Function to normalize detail page URL by removing any leading segments before '#/' before rerouting to detail page
 * @param detailPageUrl the original detail page URL
 * @returns normalized detail page URL or throw error
 */
export const getNormalizedDetailsUrl = (detailPageUrl: string) => {
  const normalizedDetailPageUrl = detailPageUrl.split('#/')
  if (normalizedDetailPageUrl?.length === 2) {
    return normalizedDetailPageUrl[1]
  } else {
    throw new Error(`Invalid detailPageUrl format: ${detailPageUrl}. Expected url with required single '#/' segment.`)
  }
}
